import type { MockDatabase, MockRecord } from './database.ts';

const migrationId = 'vqca-category-levels-1';
const requestLabMigrationId = 'vqca-request-labs-1';
const LAB_NAME = 'Houston QA Lab';

/** The agreement clauses every level repeats, matching the legacy verbiage blocks. */
const shared = `
  <p><u>Certificate of Analysis data with any respective purchase order number(s) must be input into the QC Doc Database prior to shipment of product.</u>
  A link to the QC Doc Database and <strong>registration instructions will be provided in order for the Certificate of Analysis data to be entered.</strong>
  Once a pre-shipment sample and Certificate of Analysis have been provided to Halliburton for a particular lot number, future shipments of that lot number
  require the additional purchase order number to be entered into the QC Doc Database.</p>
  <p>For packaged materials each container of product <strong>must</strong> be marked with a legible lot code.</p>
  <p>If the individual purchase order so specifies, provide an additional Certificate of Analysis with the shipment.</p>
  <p>Vendors are responsible for notifying Halliburton QC Doc administration teams of any users who no longer need access to QC Doc or for requesting new accounts. Emails can be directed to:</p>
  <blockquote>Baroid: <a href="mailto:BaroidQCDocHelp@halliburton.com">BaroidQCDocHelp@halliburton.com</a><br />
  Cementing: <a href="mailto:FDUNCementingQC@halliburton.com">FDUNCementingQC@halliburton.com</a></blockquote>
  <p>All product shipped must meet the following specifications and Certificates of Analysis will include the following information at minimum.</p>
`;

/** Level 1 is the strictest (sample every lot); level 5 relies on the vendor's own system. */
const levels: { name: string; days: number; intro: string }[] = [
  {
    name: 'VQCA Level 1', days: 5,
    intro: `<p>A 200 gram representative sample and Certificate of Analysis for each lot sold to Halliburton must be promptly sent to Halliburton QA/QC Lab,
      Halliburton Energy Services, 3000 N Sam Houston Pkwy E, Houston, TX 77032.</p>
      <p><strong>For samples shipped from outside the United States</strong> send to HALLIBURTON ENERGY SERVICE Free Zone, Attn: QA/QC Lab. Do <u>not</u>
      ship product without sample approval from Halliburton.</p>`,
  },
  {
    name: 'VQCA Level 2', days: 10,
    intro: `<p>A 200 gram representative sample and Certificate of Analysis must be sent to the Halliburton QA/QC Lab for the <strong>first three lots</strong>
      of each purchase order. Subsequent lots require a Certificate of Analysis only, unless requested by Halliburton.</p>
      <p>Product may ship once the Certificate of Analysis has been accepted; sample approval is not required for lots after the third.</p>`,
  },
  {
    name: 'VQCA Level 3', days: 15,
    intro: `<p>A Certificate of Analysis is required for every lot sold to Halliburton. Representative samples are retained by the vendor for
      <strong>twelve months</strong> and supplied to the Halliburton QA/QC Lab on request.</p>
      <p>Halliburton reserves the right to audit retained samples at any time during the agreement period.</p>`,
  },
  {
    name: 'VQCA Level 4', days: 20,
    intro: `<p>A Certificate of Analysis is required for every lot. Halliburton performs <strong>periodic verification testing</strong> on randomly selected
      lots rather than routine pre-shipment sampling.</p>
      <p>Any lot that fails verification returns the product to <u>VQCA Level 1</u> until three consecutive conforming lots are demonstrated.</p>`,
  },
  {
    name: 'VQCA Level 5', days: 30,
    intro: `<p>The vendor operates an approved quality management system and self-certifies each lot. A Certificate of Analysis must still accompany the
      shipment and be entered into the QC Doc Database.</p>
      <p>Halliburton may revoke self-certification at any time, returning the product to a lower VQCA level.</p>`,
  },
];

/**
 * The seed gives every category the same one-line verbiage, so every agreement renders the
 * same sentence. Legacy drives the agreement body from the category level attached to the
 * QA lab, so repurpose the first five into the real VQCA levels and restate the snapshots
 * already written onto agreements and requests.
 */
export function ensureVqcaCategoryLevels(database: MockDatabase): boolean {
  if (database.adminMigrations?.some((row) => row.oid === migrationId)) return false;
  const categories = database.categories ?? [];
  if (categories.length < levels.length) return false;
  const lab = (database.labs ?? []).find((row) => row.name === LAB_NAME) ?? database.labs?.[0];

  const assigned: MockRecord[] = [];
  levels.forEach((level, index) => {
    const category = categories[index];
    Object.assign(category, {
      name: level.name, levelName: level.name, catName: level.name,
      verbiage: `${level.intro}${shared}`.replace(/\s+/g, ' ').trim(),
      labId: lab?.oid ?? category.labId, qaLabName: lab?.name ?? LAB_NAME,
      isVQCACat: true, isActive: true, enabled: true,
      days: level.days, turnAroundDays: level.days,
    });
    assigned.push(category);
  });

  // Spread the seeded agreements and requests across the five levels so each one is reachable.
  const restate = (table: string, idField: string, nameFields: string[], withVerbiage: boolean) => {
    (database[table] ?? []).forEach((row, index) => {
      const level = assigned[index % assigned.length];
      row[idField] = level.oid;
      for (const field of nameFields) row[field] = level.name;
      if (withVerbiage) row.verbiage = level.verbiage;
      row.labId = level.labId;
      row.qaLab = level.qaLabName;
    });
  };
  restate('vqcas', 'catLevelId', ['catName', 'catLevelName', 'categoryLevelName'], true);
  restate('vendorRequests', 'catLevelId', ['catName', 'catLevelName'], false);

  (database.adminMigrations ??= []).push({ oid: migrationId, appliedAt: new Date().toISOString() });
  return true;
}

/**
 * `NewVendorRequest.LabId` is resolved through its CategoryLevel in the legacy
 * request query. Backfill persisted prototype rows created before that derivation
 * was represented in the mock API so reopening them still shows the selected lab.
 */
export function ensureVqcaRequestLabs(database: MockDatabase): boolean {
  if (database.adminMigrations?.some((row) => row.oid === requestLabMigrationId)) return false;

  let changed = false;
  for (const request of database.vendorRequests ?? []) {
    if (request.labId) continue;
    const category = database.categories?.find((row) => row.oid === request.catLevelId);
    if (!category?.labId) continue;
    const lab = database.labs?.find((row) => row.oid === category.labId);
    request.labId = category.labId;
    request.qaLab = lab?.name;
    changed = true;
  }

  (database.adminMigrations ??= []).push({ oid: requestLabMigrationId, appliedAt: new Date().toISOString() });
  return changed;
}
